import React, { Fragment } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { getBackgroundTheme, getTextColor } from '../../style';
import { addCommas, isDarkMode } from '../../utils/helpers';
import '../../style/customSWui.css';
import { handlePackagesVysionEvent } from '../../utils/vysion/packages';
import { VYSION_ACTIONS } from '../../utils/vysion';
import { PackagesClickVysionEventsEnum } from '../../utils/vysion/packages/click';
import CheckIcon from '../icons/CheckIcon';
import { useApp } from '../../contexts/AppContext';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { createFluidTypography } from '../../theme/material/helpers';
import breakpoints from 'theme/material/breakpoints';

const slideInFromRightAnimation = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedCheckIcon = styled(CheckIcon)`
  animation: ${slideInFromRightAnimation} 0.2s ease-in-out;
`;

const PackageContainer = styled('div')({
  paddingRight: 20,
  paddingBottom: 8,
  paddingLeft: 20,
  minHeight: 'auto',
});

const Card = styled('div')<{ selected: boolean }>(({ selected }) => ({
  width: '47%',
  marginTop: 'auto !important',
  cursor: 'pointer',
  borderRadius: 7,
  backgroundColor: getBackgroundTheme().packageCard,
  boxShadow: selected ? 'none' : '0px 3px 3px 0px rgba(0, 0, 0, 0.25)',
  border: selected ? 'solid 2px #E2C92D' : 'none',
  overflow: 'hidden',
  textAlign: 'center',
  float: 'left',
  position: 'relative',
  lineHeight: '30px',
  margin: selected ? '-6px 1%' : '0 1%',
  padding: '10px 0 0 !important',
}));

const CreditsPerCurrency = styled('b')(() => ({
  ...createFluidTypography(18, 26),
  color: getBackgroundTheme().greyText,
}));

const CreditContainer = styled('div')({
  paddingTop: 0,
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const CreditsTitle = styled('div')(() => ({
  fontWeight: 900,
  color: `#F6F4F4`,
  textAlign: 'center',
  marginTop: 30,
  marginBottom: 30,
  ...createFluidTypography(24, 36),
  [breakpoints.down(479.05)]: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 0,
    marginBottom: 0,
  },
  [breakpoints.down(768.05)]: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10,
    marginBottom: 15,
  },
}));

const CreditSubtitle = styled('span')(() => ({
  ...createFluidTypography(12, 36),
  color: isDarkMode() ? `#AFB0B1` : `#595959`,
  fontWeight: 600,
  lineHeight: '12px',
  marginBottom: 10,
  marginLeft: 5,
  [breakpoints.down(834.05)]: {
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,
    marginLeft: 0,
    marginBottom: 5,
    marginTop: 5,
  },
  [breakpoints.down(768.05)]: {
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,
    marginLeft: 0,
    marginBottom: 5,
    marginTop: 5,
  },
  [breakpoints.down(479.05)]: {
    display: 'flex',
    flexDirection: 'column',
    fontWeight: 400,
    marginLeft: 0,
    marginTop: 6,
    marginBottom: -1,
  },
}));

const PackageInfoLabel = styled('label')(() => ({
  ...createFluidTypography(14, 16),
  fontWeight: 700,
  color: isDarkMode() ? '#CFCFCF' : '#353946',
  marginTop: 15,
  marginBottom: 15,
  [breakpoints.down(768.05)]: {
    marginBottom: 12,
    marginTop: 5,
  },
  [breakpoints.down(479.05)]: {
    marginBottom: 3,
    marginTop: 0,
  },
}));

const DashedSeparator = styled('hr')({
  border: isDarkMode() ? '1px dashed #6c6c6c' : '1px dashed rgba(14, 29, 64, 0.2)',
});

const Amount = styled('div')((): any => ({
  ...createFluidTypography(18, 26),
  fontWeight: '700',
  color: getTextColor().DARK,
  textAlign: 'center',
  lineHeight: '20px',
  margin: '0 auto',
  marginTop: 30,
  marginBottom: 20,
  [breakpoints.down(834.05)]: {
    marginTop: 15,
    marginBottom: 20,
  },
  [breakpoints.down(768.05)]: {
    marginTop: 0,
    marginBottom: 5,
  },
  [breakpoints.down(479.05)]: {
    marginTop: -2,
    marginBottom: -1,
  },
}));

const PackageInfo = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ButtonContainer = styled('div')({
  paddingTop: 0,
  textAlign: 'center',
  display: 'flex',

  [breakpoints.down(834.05)]: {
    paddingTop: 0,
  },
  [breakpoints.down(768.05)]: {
    paddingTop: 10,
  },
  [breakpoints.down(479.05)]: {
    paddingTop: 5,
  },
});

const renderCreditsPerCurrency = (
  credits,
  hasTopTag,
  price,
  currencyCode = '$',
  showDecimalPrice = false,
  isMobile
) => {
  const newPrice = (price / credits).toFixed(2);
  const creditsText = `${currencyCode}${newPrice}`;
  const totaPrice = showDecimalPrice ? `${currencyCode}${price}` : `${currencyCode}${parseInt(price).toFixed()}`;

  return isMobile ? (
    <div style={{ display: 'flex', flexDirection: 'column', marginTop: 1 }}>
      <CreditsPerCurrency>{creditsText}</CreditsPerCurrency>
      <small style={{ fontWeight: 'normal', fontSize: 12, letterSpacing: -0.24, lineHeight: '12px', marginTop: 3 }}>
        per credit / {totaPrice} total
      </small>
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <CreditsPerCurrency>{`${creditsText} / Credit`}</CreditsPerCurrency>
      <span style={{ fontWeight: 400, fontSize: 16, paddingTop: 10 }}>{`${totaPrice}`} total</span>
    </div>
  );
};

interface BillingPackagePromoVariantProps {
  selectPackage: any;
  credits: any;
  identifier: any;
  currencyCharCode: any;
  price: any;
  index: any;
  variant: 'vip' | 'special' | 'intro_credit_promo';
}

const BillingPackagePromoVariant = ({
  selectPackage,
  credits,
  identifier,
  currencyCharCode,
  price,
  index,
  variant,
}: BillingPackagePromoVariantProps): any => {
  const { isMobile } = useMediaBreakpoints();
  const { selectedSpecialOrVipPromo, setSelectedSpecialOrVipPromo } = useApp();

  const variantStyle = () => {
    switch (variant) {
      case 'vip':
        return {
          color: '#E21D4A',
        };
      case 'special':
        return {
          color: '#2B8FD7',
        };
      case 'intro_credit_promo':
        return {
          color: '#DD0B24',
        };
    }
  };

  const renderPackageInfo = () => {
    switch (parseInt(credits)) {
      case 1500: {
        return 'Save 60%!';
      }
      case 300: {
        return 'Save 34%!';
      }
      case 250: {
        return variant === 'vip' ? 'Save 34%!' : 'Save 20%!';
      }
      case 20: {
        return isMobile ? 'Get Started!' : 'Special Welcome Credit Pack!';
      }
    }
  };

  const introCreditCustomStyle = {
    packageInfo: {
      paddingBottom: 8,
    },
    packageInfoLabel: {
      fontSize: 13,
      marginBottom: 0,
      letterSpacing: -0.26,
      lineHeight: '15px',
      marginTop: 6,
    },
    packageCredit: {
      lineHeight: '18px',
    },
  };

  return (
    <Card
      id={`package-${variant}-${index}`}
      data-test-id={`package-${variant}-${index}`}
      onClick={() => {
        setSelectedSpecialOrVipPromo(identifier);
        selectPackage(identifier);
        handlePackagesVysionEvent({
          action: VYSION_ACTIONS.CLICK,
          event: PackagesClickVysionEventsEnum.CLICK_PACKAGE_SELECTED_PACKAGE,
          component: `${credits} credits`,
          newEventName: `packages-${credits}credits`,
        });
      }}
      selected={selectedSpecialOrVipPromo === identifier}
    >
      <PackageContainer>
        <CreditContainer>
          <CreditsTitle style={{ color: selectedSpecialOrVipPromo === identifier ? variantStyle().color : 'unset' }}>
            <b
              style={{
                fontWeight: 'bolder',
                ...(variant === 'intro_credit_promo' && isMobile ? introCreditCustomStyle.packageCredit : {}),
              }}
            >
              {addCommas(credits)}
            </b>
            <CreditSubtitle
              style={selectedSpecialOrVipPromo === identifier && !isMobile ? { color: variantStyle().color } : {}}
            >
              Credits
            </CreditSubtitle>
          </CreditsTitle>
          <DashedSeparator />
        </CreditContainer>

        {isMobile ? (
          <Fragment>
            <ButtonContainer>
              <Amount>
                {renderCreditsPerCurrency(
                  credits,
                  true,
                  price,
                  String.fromCharCode(currencyCharCode),
                  variant === 'intro_credit_promo',
                  isMobile
                )}
              </Amount>
            </ButtonContainer>
            <DashedSeparator style={{ marginBottom: 9, marginTop: 12 }} />

            <PackageInfo style={variant === 'intro_credit_promo' ? introCreditCustomStyle.packageInfo : {}}>
              {selectedSpecialOrVipPromo === identifier && (
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                <AnimatedCheckIcon customStyle={{ marginRight: 10 }} strokeColor={variantStyle().color} />
              )}
              <PackageInfoLabel style={variant === 'intro_credit_promo' ? introCreditCustomStyle.packageInfoLabel : {}}>
                {renderPackageInfo()}
              </PackageInfoLabel>
            </PackageInfo>
          </Fragment>
        ) : (
          <Fragment>
            <PackageInfo>
              {selectedSpecialOrVipPromo === identifier && (
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                <AnimatedCheckIcon customStyle={{ marginRight: 10 }} strokeColor={variantStyle().color} />
              )}
              <PackageInfoLabel>{renderPackageInfo()}</PackageInfoLabel>
            </PackageInfo>
            <DashedSeparator />
            <Amount>
              {renderCreditsPerCurrency(
                credits,
                true,
                price,
                String.fromCharCode(currencyCharCode),
                variant === 'intro_credit_promo',
                isMobile
              )}
            </Amount>
          </Fragment>
        )}
      </PackageContainer>
    </Card>
  );
};

export default BillingPackagePromoVariant;
